import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import AppLayout from '../layouts/AppLayout';
import ProtectedRoute from '../components/routing/ProtectedRoute';
import RoleGuard from '../components/routing/RoleGuard';
import LoadingState from '../components/common/LoadingState';
import HomePageWrapper from '../pages/HomePageWrapper';
import LoginPageWrapper from '../pages/LoginPageWrapper';

const TripsPage = lazy(() => import('../components/TripsPage'));
const BookingsPage = lazy(() => import('../components/BookingsPage'));
const WalletPage = lazy(() => import('../components/WalletPage'));
const NotificationsPage = lazy(() => import('../components/NotificationsPage'));
const ProfilePage = lazy(() => import('../pages/ProfilePageWrapper'));
const AgencyDashboard = lazy(() => import('../components/AgencyDashboard'));
const AgencyLeaderboard = lazy(() => import('../components/AgencyLeaderboard'));
const AdminPanel = lazy(() => import('../components/AdminPanel'));
const UserBrowserPage = lazy(() => import('../pages/UserBrowserPage'));
const PublicProfilePage = lazy(() => import('../pages/PublicProfilePage'));
const ChangeHistoryPage = lazy(() => import('../pages/ChangeHistoryPage'));
const TripReviewPage = lazy(() => import('../pages/TripReviewPage'));

const withSuspense = (node: React.ReactNode) => (
  <Suspense fallback={<LoadingState fullPage />}>{node}</Suspense>
);

const AppRoutes: React.FC = () => (
  <Routes>
    <Route path="/login" element={<LoginPageWrapper />} />

    <Route
      element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }
    >
      <Route index element={<HomePageWrapper />} />
      <Route path="trips" element={withSuspense(<TripsPage />)} />
      <Route path="bookings" element={withSuspense(<BookingsPage />)} />
      <Route path="reservations" element={<Navigate to="/bookings" replace />} />
      <Route path="wallet" element={withSuspense(<WalletPage />)} />
      <Route path="notifications" element={withSuspense(<NotificationsPage />)} />
      <Route
        path="profile"
        element={withSuspense(<ProfilePage />)}
      />

      <Route
        path="agency"
        element={
          <RoleGuard allowedRoles={['agency', 'admin']}>
            {withSuspense(<AgencyDashboard />)}
          </RoleGuard>
        }
      />
      <Route
        path="agency/leaderboard"
        element={
          <RoleGuard allowedRoles={['agency', 'admin']}>
            {withSuspense(<AgencyLeaderboard />)}
          </RoleGuard>
        }
      />

      <Route
        path="admin"
        element={
          <RoleGuard allowedRoles={['admin']}>
            {withSuspense(<AdminPanel />)}
          </RoleGuard>
        }
      />
      <Route
        path="admin/users"
        element={
          <RoleGuard allowedRoles={['admin']}>
            {withSuspense(<AdminPanel />)}
          </RoleGuard>
        }
      />
      <Route
        path="admin/users/browse"
        element={
          <RoleGuard allowedRoles={['admin']}>
            {withSuspense(<UserBrowserPage />)}
          </RoleGuard>
        }
      />
      <Route
        path="users/:userId"
        element={withSuspense(<PublicProfilePage />)}
      />
      <Route
        path="trips/:tripId/reviews"
        element={withSuspense(<TripReviewPage />)}
      />
      <Route
        path="admin/history"
        element={
          <RoleGuard allowedRoles={['admin']}>
            {withSuspense(<ChangeHistoryPage />)}
          </RoleGuard>
        }
      />
    </Route>

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

export default AppRoutes;
